// src/features/AdminDashboard/Pages/SummaryReportPage.tsx

import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Loader2,
  X,
  XCircle,
} from 'lucide-react';

import {
  useEffect,
  useState,
} from 'react';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import {
  exportSummaryReportExcel,
} from '../reports/services/publicReport.service';

import {
  getTodayDate,
} from '../reports/utils/reportDate.utils';


// ============================================================
// TOAST TYPES
// ============================================================

type ToastType =
  | 'success'
  | 'error';


interface ToastState {
  type: ToastType;
  message: string;
}


// ============================================================
// PAGE
// ============================================================

const SummaryReportPage = () => {

  const navigate =
    useNavigate();

  const location =
    useLocation();


  // ==========================================================
  // TODAY
  // ==========================================================
  //
  // Every time this page is entered:
  //
  // From Date = Today
  // To Date   = Today
  //
  // Example:
  //
  // 2026-10-06
  // 2026-10-06
  // ==========================================================

  const today =
    getTodayDate();


  // ==========================================================
  // DATE STATE
  // ==========================================================

  const [
    fromDate,
    setFromDate,
  ] = useState<string>(
    today,
  );

  const [
    toDate,
    setToDate,
  ] = useState<string>(
    today,
  );


  // ==========================================================
  // EXPORT STATE
  // ==========================================================

  const [
    isExporting,
    setIsExporting,
  ] = useState<boolean>(
    false,
  );


  // ==========================================================
  // TOAST STATE
  // ==========================================================

  const [
    toast,
    setToast,
  ] = useState<ToastState | null>(
    null,
  );


  // ==========================================================
  // RESET DATES WHEN PAGE IS ENTERED
  // ==========================================================
  //
  // This ensures:
  //
  // Public Reports
  //       ↓
  // Summary Report
  //       ↓
  // Today → Today
  //
  // If user changes the dates:
  //
  // 01-10-2026 → 06-10-2026
  //
  // and goes back:
  //
  // Public Reports
  //       ↓
  // Summary Report
  //       ↓
  // Today → Today
  //
  // Previous selected dates are NOT restored.
  // ==========================================================

  useEffect(() => {

    const currentToday =
      getTodayDate();

    setFromDate(
      currentToday,
    );

    setToDate(
      currentToday,
    );

  }, [location.key]);


  // ==========================================================
  // SHOW TOAST
  // ==========================================================

  const showToast = (
    type: ToastType,
    message: string,
  ) => {

    setToast({
      type,
      message,
    });
  };


  // ==========================================================
  // AUTO CLOSE TOAST
  // ==========================================================

  useEffect(() => {

    if (!toast) {
      return;
    }

    const timer =
      window.setTimeout(
        () => {
          setToast(null);
        },
        4000,
      );

    return () => {
      window.clearTimeout(
        timer,
      );
    };

  }, [toast]);


  // ==========================================================
  // FROM DATE CHANGE
  // ==========================================================

  const handleFromDateChange = (
    value: string,
  ) => {

    setFromDate(
      value,
    );
  };


  // ==========================================================
  // TO DATE CHANGE
  // ==========================================================

  const handleToDateChange = (
    value: string,
  ) => {

    setToDate(
      value,
    );
  };


  // ==========================================================
  // EXPORT
  // ==========================================================

  const handleExport =
    async () => {

      // ======================================================
      // VALIDATE EMPTY DATES
      // ======================================================

      if (
        !fromDate ||
        !toDate
      ) {

        showToast(
          'error',
          'Please select both From Date and To Date.',
        );

        return;
      }


      // ======================================================
      // VALIDATE DATE RANGE
      // ======================================================

      if (
        fromDate >
        toDate
      ) {

        showToast(
          'error',
          'From Date cannot be greater than To Date.',
        );

        return;
      }


      // ======================================================
      // API CALL
      // ======================================================

      try {

        setIsExporting(
          true,
        );

        await exportSummaryReportExcel({
          fromDate,
          toDate,
        });


        // ====================================================
        // SUCCESS
        // ====================================================

        showToast(
          'success',
          'Summary Report Excel downloaded successfully.',
        );

      } catch (
        exportError
      ) {

        console.error(
          'Failed to export Summary Report:',
          exportError,
        );

        showToast(
          'error',
          'Failed to export Summary Report. Please try again.',
        );

      } finally {

        setIsExporting(
          false,
        );
      }
    };


  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <main
      className="
        min-h-screen
        bg-[#eef3f8]
        px-4
        py-5
        sm:px-6
        lg:px-8
      "
    >

      {/* ====================================================
          TOAST
      ===================================================== */}

      {toast && (

        <div
          className="
            fixed
            right-4
            top-5
            z-[9999]
            w-[calc(100%-2rem)]
            max-w-[420px]
          "
        >

          <div
            className={`
              flex
              items-start
              gap-3
              rounded-xl
              border
              bg-white
              px-4
              py-3
              shadow-xl
              ${
                toast.type === 'success'
                  ? 'border-emerald-200'
                  : 'border-red-200'
              }
            `}
          >

            {/* ============================================
                TOAST ICON
            ============================================= */}

            {toast.type === 'success' ? (

              <CheckCircle2
                size={21}
                className="
                  mt-0.5
                  shrink-0
                  text-emerald-600
                "
              />

            ) : (

              <XCircle
                size={21}
                className="
                  mt-0.5
                  shrink-0
                  text-red-600
                "
              />

            )}


            {/* ============================================
                MESSAGE
            ============================================= */}

            <div
              className="
                min-w-0
                flex-1
              "
            >

              <p
                className={`
                  text-sm
                  font-medium
                  ${
                    toast.type === 'success'
                      ? 'text-emerald-800'
                      : 'text-red-700'
                  }
                `}
              >
                {toast.message}
              </p>

            </div>


            {/* ============================================
                CLOSE
            ============================================= */}

            <button
              type="button"
              onClick={() =>
                setToast(null)
              }
              className="
                shrink-0
                text-slate-400
                transition
                hover:text-slate-700
              "
              aria-label="Close notification"
            >

              <X
                size={17}
              />

            </button>

          </div>

        </div>

      )}


      {/* ====================================================
          MAIN CONTAINER
      ===================================================== */}

      <div
        className="
          mx-auto
          w-full
          max-w-[1440px]
        "
      >

        {/* ==================================================
            HEADER
        ================================================== */}

        <section
          className="
            mb-5
            rounded-xl
            border
            border-slate-200
            bg-white
            shadow-sm
          "
        >

          <div
            className="
              flex
              items-center
              gap-3
              px-4
              py-4
              sm:px-5
            "
          >

            {/* ==============================================
                BACK BUTTON
            ============================================== */}

            <button
              type="button"
              onClick={() =>
                navigate(
                  '/public-report',
                )
              }
              className="
                flex
                h-10
                w-10
                shrink-0
                items-center
                justify-center
                rounded-lg
                border
                border-slate-200
                bg-white
                text-slate-500
                transition
                hover:border-teal-200
                hover:bg-teal-50
                hover:text-teal-600
                focus:outline-none
                focus:ring-2
                focus:ring-teal-100
              "
              aria-label="Back to public reports"
            >

              <ArrowLeft
                size={18}
                strokeWidth={1.8}
              />

            </button>


            {/* ==============================================
                HEADER TEXT
            ============================================== */}

            <div>

              <h1
                className="
                  text-lg
                  font-semibold
                  text-slate-900
                  sm:text-xl
                "
              >
                Summary Report
              </h1>

              <p
                className="
                  mt-0.5
                  text-sm
                  text-slate-500
                "
              >
                Select the reporting period.
              </p>

            </div>

          </div>

        </section>


        {/* ==================================================
            FILTER CARD
        ================================================== */}

        <section
          className="
            rounded-xl
            border
            border-slate-200
            bg-white
            p-4
            shadow-sm
            sm:p-5
          "
        >

          <div
            className="
              grid
              grid-cols-1
              gap-4
              md:grid-cols-2
            "
          >

            {/* ==============================================
                FROM DATE
            ============================================== */}

            <div>

              <label
                htmlFor="summary-from-date"
                className="
                  mb-1.5
                  block
                  text-xs
                  font-medium
                  text-slate-700
                "
              >
                From Date
              </label>

              <input
                id="summary-from-date"
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(event) =>
                  handleFromDateChange(
                    event.target.value,
                  )
                }
                className="
                  h-10
                  w-full
                  rounded-lg
                  border
                  border-slate-200
                  bg-white
                  px-3
                  text-sm
                  text-slate-700
                  outline-none
                  transition
                  focus:border-teal-500
                  focus:ring-2
                  focus:ring-teal-100
                "
              />

            </div>


            {/* ==============================================
                TO DATE
            ============================================== */}

            <div>

              <label
                htmlFor="summary-to-date"
                className="
                  mb-1.5
                  block
                  text-xs
                  font-medium
                  text-slate-700
                "
              >
                To Date
              </label>

              <input
                id="summary-to-date"
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(event) =>
                  handleToDateChange(
                    event.target.value,
                  )
                }
                className="
                  h-10
                  w-full
                  rounded-lg
                  border
                  border-slate-200
                  bg-white
                  px-3
                  text-sm
                  text-slate-700
                  outline-none
                  transition
                  focus:border-teal-500
                  focus:ring-2
                  focus:ring-teal-100
                "
              />

            </div>

          </div>


          {/* ==================================================
              EXPORT BUTTON
          ================================================== */}

          <div
            className="
              mt-5
              flex
              justify-end
              border-t
              border-slate-100
              pt-4
            "
          >

            <button
              type="button"
              onClick={
                handleExport
              }
              disabled={
                isExporting
              }
              className="
                inline-flex
                h-10
                items-center
                justify-center
                gap-2
                rounded-lg
                bg-teal-600
                px-5
                text-sm
                font-medium
                text-white
                transition
                hover:bg-teal-700
                focus:outline-none
                focus:ring-2
                focus:ring-teal-500
                focus:ring-offset-2
                disabled:cursor-not-allowed
                disabled:opacity-60
              "
            >

              {isExporting ? (

                <>
                  <Loader2
                    size={16}
                    className="animate-spin"
                  />

                  Exporting...
                </>

              ) : (

                <>
                  <Download
                    size={16}
                    strokeWidth={2}
                  />

                  Export Excel
                </>

              )}

            </button>

          </div>

        </section>

      </div>

    </main>
  );
};


export default SummaryReportPage;